import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Header from "../components/Header";
import CadastroEmpresaModal from "../components/CadastroEmpresaModal";
import Stars from "../components/Stars";
import ShareButtons from "../components/ShareButtons";
import ComplaintList from "../components/ComplaintList";
import { HtmlEditor, WhatsappEditor, TAGS } from "../components/RichEditors";
import { obterEmpresa, responderDenuncia, avaliarDenuncia, confirmarRetorno, contestarDenuncia, treplicarDenuncia, reportarDenuncia, confirmarRelato, contestarEmpresa, MOTIVOS_REPORTE, adminAtualizarRelato, urlImagemAtualizacao, responderRelato, adminEmailResponsavel, adminWhatsappResponsavel, adminVisibilidadeAtualizacao, adminApagarAtualizacao, adminListarModelos, adminCriarModelo, adminListarModelosWpp, adminCriarModeloWpp, empresaLogoUrl } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { useComplaintModal } from "../context/ComplaintModalContext";

const SELO = {
  ra:   { txt: "Selo eaiRH", cls: "bg-green-100 text-green-800" },
  good: { txt: "Boa reputação", cls: "bg-blue-100 text-blue-800" },
  mid:  { txt: "Reputação regular", cls: "bg-yellow-100 text-yellow-800" },
  bad:  { txt: "Não recomendada", cls: "bg-red-100 text-red-800" },
  none: { txt: "Sem reputação", cls: "bg-gray-100 text-gray-600" },
};

function corNota(s) { return s >= 7 ? "text-green-600" : s >= 5 ? "text-yellow-600" : "text-red-600"; }
function fmt(iso) { try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return ""; } }

// Filtros por status da lista de reclamações (estilo Reclame Aqui).
const FILTROS_STATUS = [
  { k: "todas", t: "Todas", fn: () => true },
  { k: "naoresp", t: "Não respondidas", fn: (d) => !d.resposta },
  { k: "resp", t: "Respondidas", fn: (d) => !!d.resposta },
  { k: "resolv", t: "Resolvidas", fn: (d) => d.avaliacao?.resolvido === true },
];

export default function CompanyPage() {
  // Só os dígitos do começo: link colado em texto chega com pontuação grudada. Em 10/09 a mensagem para a
  // Reframax dizia "(eairh.com.br/empresas/442), com…" e o LinkedIn abriu "/empresas/442)," — página quebrada
  // justo para a empresa que tínhamos acabado de convidar.
  const { id: idBruto } = useParams();
  const id = (String(idBruto ?? "").match(/^\d+/) || [idBruto])[0];
  const navigate = useNavigate();
  const { user, isEmpresa, reivindicar } = useAuth();
  const { openComplaint } = useComplaintModal();
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(null);
  const [reivErro, setReivErro] = useState(null);
  const [cadEmpresa, setCadEmpresa] = useState(false); // popup de cadastro de empresa (pré-preenchido com esta empresa)
  const [filtroStatus, setFiltroStatus] = useState("todas"); // filtro da lista de reclamações (estilo Reclame Aqui)

  const carregar = useCallback(async () => {
    try { setDados(await obterEmpresa(id)); }
    catch (e) { setErro(e.message); }
  }, [id]);

  useEffect(() => { carregar(); }, [carregar]);

  if (erro) return <Tela><div className="text-red-600">{erro}</div></Tela>;
  if (!dados) return <Tela><div className="text-gray-500">Carregando...</div></Tela>;

  const { empresa, denuncias, indiceRetorno } = dados;
  const selo = SELO[empresa.selo] || SELO.none;
  const souDonoDestaEmpresa = isEmpresa && user?.empresaId === empresa.id;
  // Reputação "em formação": poucos dados (nenhuma nota de candidato) → backend devolve selo "none".
  // Nesses casos o score numérico não é confiável; não mostramos o número baixo enganoso.
  const emFormacao = empresa.selo === "none";
  // Sinal positivo honesto: empresa que RESPONDE aos relatos (índice de resposta alto) — conta a favor,
  // mesmo sem nota fechada. Evita punir quem faz a coisa certa.
  const respondeBem = empresa.indiceResposta >= 80 && empresa.totalDenuncias > 0;

  return (
    <Tela>
      {/* Cabeçalho da empresa */}
      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm flex flex-col md:flex-row md:items-center gap-6">
        {empresa.temLogo && (
          <img src={empresaLogoUrl(empresa.id)} alt={`Logo ${empresa.nome}`}
            className="w-20 h-20 rounded-xl object-contain bg-white border border-gray-200 shrink-0 self-start" />
        )}
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">
            {empresa.nome}
            {empresa.verificada && <span className="ml-2 text-sm text-blue-600">✔ verificada</span>}
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            {[empresa.setor, empresa.cidade].filter(Boolean).join(" · ")}
          </p>
          {/* Principais certificações: DECLARADAS pela empresa. Ficam visualmente separadas (cinza, neutras) do selo
              de reputação — que sai dos relatos. A legenda "informado pela empresa" é o que impede a confusão. */}
          {empresa.certificacoes?.length > 0 && (
            <div className="mt-2">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs text-gray-500">Principais certificações:</span>
                {empresa.certificacoes.map((c) => (
                  <span key={c} className="text-xs font-medium rounded-md bg-gray-100 text-gray-700 border border-gray-200 px-2 py-0.5">{c}</span>
                ))}
              </div>
              <p className="text-[11px] text-gray-400 mt-0.5">informado pela empresa</p>
            </div>
          )}
          <span className={`inline-block mt-3 text-xs font-semibold rounded-full px-3 py-1 ${selo.cls}`}>
            {empresa.classificacao}
          </span>
          {respondeBem && (
            <span className="inline-block mt-3 ml-2 text-xs font-semibold rounded-full px-3 py-1 bg-emerald-100 text-emerald-800 border border-emerald-200">
              💬 Responde aos candidatos
            </span>
          )}

          {/* Selos comportamentais derivados dos relatos de retorno */}
          {empresa.alertas?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {empresa.alertas.map((a) => (
                <span key={a} className="text-xs font-semibold rounded-full bg-amber-100 text-amber-800 px-3 py-1 border border-amber-200">
                  {a}
                </span>
              ))}
            </div>
          )}

          {/* Reivindicar: usuário-empresa logado que ainda não administra esta empresa */}
          {isEmpresa && user?.empresaId !== empresa.id && (
            <div className="mt-3">
              <button
                onClick={async () => {
                  setReivErro(null);
                  try { await reivindicar(empresa.id); await carregar(); }
                  catch (e) { setReivErro(e.message); }
                }}
                className="text-sm font-semibold rounded-lg border border-primary text-primary px-3 py-1.5 hover:bg-primary hover:text-white transition"
              >
                Sou o RH desta empresa — reivindicar
              </button>
              {reivErro && <p className="text-red-600 text-xs mt-1">{reivErro}</p>}
            </div>
          )}

          {/* Convite ao lado-empresa (visitante / candidato / deslogado). O texto muda conforme a página já
              tenha dono: chamar o RH pra "assumir e responder os relatos" numa empresa cujo time já está
              dentro soa como se ninguém tivesse visto o que eles já fizeram. Com conta, o visitante certo é
              o colega que ainda não tem acesso — o cadastro com e-mail corporativo já o coloca no time. */}
          {!souDonoDestaEmpresa && !isEmpresa && (
            <div className="mt-3">
              <button
                onClick={() => setCadEmpresa(true)}
                className="text-sm font-semibold rounded-lg bg-primary text-white px-3 py-1.5 hover:bg-primary/90 transition"
              >
                {empresa.temConta
                  ? `Trabalha na ${empresa.nome}? Entre no time e acompanhe os relatos`
                  : "É o RH desta empresa? Cadastre-se e responda os relatos"}
              </button>
              <p className="text-xs text-gray-400 mt-1">
                {empresa.temConta
                  ? "Use o seu e-mail corporativo — a conta já entra ligada à página da empresa."
                  : "Empresas têm direito de resposta — e quem trata bem ganha reputação."}
              </p>
            </div>
          )}
        </div>
        <div className="text-center">
          {emFormacao ? (
            <div className="max-w-[190px]">
              <div className="text-2xl font-extrabold text-gray-700 leading-tight">Reputação<br/>em formação</div>
              <div className="text-xs text-gray-500 mt-1.5">poucos relatos ainda — a nota aparece conforme mais candidatos avaliam</div>
            </div>
          ) : (
            <>
              <div className={`text-5xl font-extrabold ${corNota(empresa.score)}`}>{empresa.score.toFixed(1)}</div>
              {(empresa.totalDenuncias + (empresa.totalElogios || 0)) > 0 && <div className="mt-1"><Stars score={empresa.score} size={20} /></div>}
              <div className="text-xs text-gray-500 mt-0.5">índice eaiRH</div>
            </>
          )}
        </div>
      </div>

      {/* Métricas */}
      <div className={`grid grid-cols-2 gap-3 mt-4 ${empresa.totalElogios > 0 ? "md:grid-cols-5" : "md:grid-cols-4"}`}>
        {/* Elogio tem placar próprio: sem isto, empresa só elogiada aparecia com "0 relatos". */}
        <Metrica v={empresa.totalDenuncias} l={empresa.totalElogios > 0 ? "reclamações" : "relatos"} />
        {empresa.totalElogios > 0 && <Metrica v={empresa.totalElogios} l="elogios" cor="text-emerald-600" />}
        {/* Sem relato que peça resposta, "0% deram retorno" em vermelho é uma acusação sem base: acontece,
            por exemplo, com empresa cujo único registro é um ELOGIO. Nesse caso mostramos "–", como já se faz
            com a nota média em reputação em formação. */}
        {empresa.totalDenuncias > 0
          ? <Metrica v={`${empresa.indiceResposta}%`} l="deram retorno" cor={empresa.indiceResposta >= 60 ? "text-green-600" : "text-red-600"} />
          : <Metrica v="–" l="deram retorno" />}
        {/* A nota média CRUA aparece sempre que houver avaliação — antes dependia do selo, e empresa
            avaliada com 8 exibia "–" como se ninguém a tivesse avaliado. */}
        <Metrica v={empresa.notaMedia > 0 ? empresa.notaMedia.toFixed(1) : "–"} l="nota média" />
        <Metrica v={`${empresa.indiceVoltaria}%`} l="voltariam" />
      </div>

      {/* ÍNDICE DE RETORNO — fica FORA do bloco de métricas de propósito.
          As outras métricas saem dos relatos: gente que veio escrever, quase sempre porque se deu mal. Esta sai
          de todo mundo que se candidatou por aqui e respondeu a nossa pergunta em três cliques — é censo, não
          denúncia. Misturar as duas no mesmo bloco faria parecer que saem da mesma fonte. */}
      {indiceRetorno && (
        <div className="mt-4 rounded-2xl border border-sky-200 bg-sky-50 p-4">
          <p className="text-gray-900">
            <b>{indiceRetorno.receberamRetorno} de {indiceRetorno.responderam}</b> pessoas que se candidataram
            pelo eaiRH disseram que a {empresa.nome} <b>deu retorno</b>.
          </p>
          <p className="text-sm text-gray-600 mt-1">
            {indiceRetorno.percentual}% — perguntamos a quem se candidatou por aqui, não só a quem veio reclamar.
          </p>
        </div>
      )}

      {/* CTA de relato (candidato/visitante): converte quem CONSULTA a reputação em quem CONTRIBUI — ataca o gap
          do funil (gente vê reputação sem se cadastrar). Não aparece p/ conta de empresa. */}
      {!isEmpresa && (
        <div className="mt-4 rounded-2xl border border-orange-200 bg-orange-50 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <p className="font-semibold text-gray-900">Passou por um processo seletivo na {empresa.nome}?</p>
            <p className="text-sm text-gray-600">Conte como foi — leva 2 min e ajuda o próximo candidato. Pode ser anônimo.</p>
          </div>
          <button onClick={() => openComplaint({ empresa: empresa.nome, empresaId: empresa.id })}
            className="shrink-0 rounded-xl bg-orange-600 hover:bg-orange-700 text-white font-semibold px-5 py-2.5">
            Registrar meu relato
          </button>
        </div>
      )}

      {/* Dono em análise: avisa que as ações (responder/tréplica/contestar) só liberam após a aprovação — consistente
          com o painel e as páginas de conta/publicar vaga. O backend é a fonte de verdade (rejeita ação não-verificada). */}
      {souDonoDestaEmpresa && empresa.verificada === false && (
        <div className="mt-6 rounded-xl border border-amber-300 bg-amber-50 p-4 text-amber-800 text-sm">
          <b>⏳ Sua empresa está em análise.</b> Você já acompanha os relatos, mas só poderá <b>responder, dar tréplica ou contestar</b> após a verificação do eaiRH.
        </div>
      )}

      {/* Denúncias */}
      <h2 className="text-xl font-bold text-gray-900 mt-8 mb-3">Relatos ({denuncias.length})</h2>

      {/* Filtros por status (estilo Reclame Aqui) */}
      {denuncias.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          {FILTROS_STATUS.map((f) => {
            const n = denuncias.filter(f.fn).length;
            const ativo = filtroStatus === f.k;
            return (
              <button key={f.k} type="button" onClick={() => setFiltroStatus(f.k)}
                className={`text-sm font-semibold rounded-full px-3 py-1.5 border transition ${ativo ? "bg-primary text-white border-primary" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"}`}>
                {f.t} <span className={ativo ? "text-white/80" : "text-gray-400"}>({n})</span>
              </button>
            );
          })}
        </div>
      )}

      {(() => {
        const fn = (FILTROS_STATUS.find((f) => f.k === filtroStatus) || FILTROS_STATUS[0]).fn;
        const lista = denuncias.filter(fn);
        if (lista.length === 0) return <p className="text-gray-500 text-sm py-4">Nenhum relato neste filtro.</p>;
        // Lista enxuta (resumo + paginação) que ESCALA; o clique abre a reclamação na íntegra (/relato/:id),
        // onde mora a história completa, a resposta da empresa e as ações. Modelo Reclame Aqui.
        return <ComplaintList complaints={lista} onSelect={(o) => navigate(`/relato/${o.id}`)} hideEmpresa />;
      })()}

      <button onClick={() => navigate("/")} className="mt-6 text-primary font-semibold">← Voltar</button>

      <CadastroEmpresaModal open={cadEmpresa} onClose={() => setCadEmpresa(false)} empresaNome={empresa.nome} />
    </Tela>
  );
}

function Tela({ children }) {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-4xl mx-auto px-4 md:px-6 py-8 pb-28 md:pb-8">{children}</main>
    </div>
  );
}

function Metrica({ v, l, cor = "text-gray-900" }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 text-center">
      <div className={`text-2xl font-bold ${cor}`}>{v}</div>
      <div className="text-xs text-gray-500 mt-0.5">{l}</div>
    </div>
  );
}

// Timeline "Atualizações da eaiRH" no relato + (para admin) a caixa de publicar uma nova atualização.
// Públicas aparecem para todos; privadas só o autor recebe (o backend já filtra o que vem no DTO).
function AtualizacoesEaiRH({ d, isAdmin, onAcao }) {
  const [texto, setTexto] = useState("");
  const [publica, setPublica] = useState(false); // padrão PRIVADO ("só o candidato") — evita publicar sem querer; admin marca "Pública" de propósito
  const [imagem, setImagem] = useState(null);
  const [preview, setPreview] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [msg, setMsg] = useState(null);
  const [erro, setErro] = useState(null);
  const [zoom, setZoom] = useState(null); // URL do print aberto no lightbox
  const [acaoId, setAcaoId] = useState(null); // id da atualização em ação (apagar/visibilidade)

  // Admin: alterna público <-> privado (privado = "ocultar do público", só o candidato vê).
  const alternarVisibilidade = async (a) => {
    setAcaoId(a.id); setErro(null);
    try { await adminVisibilidadeAtualizacao(d.id, a.id, !a.publica); onAcao && onAcao(); }
    catch (e) { setErro(e.message || "Falha ao alterar visibilidade."); }
    finally { setAcaoId(null); }
  };
  // Admin: apaga a atualização de vez.
  const apagarAtualizacao = async (a) => {
    if (!window.confirm("Apagar esta atualização? Esta ação não pode ser desfeita.")) return;
    setAcaoId(a.id); setErro(null);
    try { await adminApagarAtualizacao(d.id, a.id); onAcao && onAcao(); }
    catch (e) { setErro(e.message || "Falha ao apagar."); }
    finally { setAcaoId(null); }
  };

  // Fecha o lightbox com Esc.
  useEffect(() => {
    if (!zoom) return;
    const onKey = (e) => { if (e.key === "Escape") setZoom(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoom]);

  const anexar = (file) => {
    if (!file) return;
    if (!/^image\/(jpeg|png|webp|gif)$/.test(file.type)) { setErro("Use uma imagem JPG, PNG, WEBP ou GIF."); return; }
    if (file.size > 5 * 1024 * 1024) { setErro("A imagem excede 5 MB."); return; }
    setErro(null); setImagem(file); setPreview(URL.createObjectURL(file));
  };
  const onPaste = (e) => {
    const item = [...(e.clipboardData?.items || [])].find((i) => i.type.startsWith("image/"));
    if (item) { e.preventDefault(); anexar(item.getAsFile()); }
  };
  const publicar = async () => {
    if (!texto.trim()) { setErro("Escreva o texto da atualização."); return; }
    setEnviando(true); setErro(null); setMsg(null);
    try {
      const r = await adminAtualizarRelato(d.id, { texto: texto.trim(), publica, imagem });
      const canais = [];
      if (r.emailEnviado) canais.push("e-mail");
      canais.push("sino");
      if (r.whatsappTentado) canais.push("WhatsApp");
      let m = `Publicado ✅ — candidato avisado por ${canais.join(", ")}.`;
      if (r.autorTemEmail === false) m += " (sem e-mail no cadastro)";
      setMsg(m);
      setTexto(""); setImagem(null); setPreview(null);
      onAcao && onAcao();
    } catch (e) { setErro(e.message || "Falha ao publicar."); }
    finally { setEnviando(false); }
  };

  // Resposta do CANDIDATO (autor do relato) às atualizações da eaiRH.
  const [respTexto, setRespTexto] = useState("");
  const [respBusy, setRespBusy] = useState(false);
  const [respMsg, setRespMsg] = useState(null);
  const [respErro, setRespErro] = useState(null);
  const responder = async () => {
    if (!respTexto.trim()) { setRespErro("Escreva sua resposta."); return; }
    setRespBusy(true); setRespErro(null); setRespMsg(null);
    try {
      await responderRelato(d.id, respTexto.trim());
      setRespMsg("Resposta enviada ✅ — a equipe eaiRH foi avisada.");
      setRespTexto("");
      onAcao && onAcao();
    } catch (e) { setRespErro(e.message || "Falha ao enviar."); }
    finally { setRespBusy(false); }
  };

  // Admin: e-mail livre ao RESPONSÁVEL do processo (contato do relato), em nome da eaiRH.
  const [emailAberto, setEmailAberto] = useState(false);
  const [emAssunto, setEmAssunto] = useState("");
  const [emCorpo, setEmCorpo] = useState("");
  const [emCopiar, setEmCopiar] = useState(false);
  const [emBusy, setEmBusy] = useState(false);
  const [emMsg, setEmMsg] = useState(null);
  const [emErro, setEmErro] = useState(null);
  // Modelos de e-mail (caso "EmpresaCitada") para preencher o compositor num clique + salvar novos.
  const [emModelos, setEmModelos] = useState([]);
  const [emModeloKey, setEmModeloKey] = useState(0); // muda p/ remontar o HtmlEditor ao trocar de modelo
  useEffect(() => {
    if (!emailAberto) return;
    adminListarModelos().then((ms) => setEmModelos((ms || []).filter((m) => m.caso === "EmpresaCitada"))).catch(() => {});
  }, [emailAberto]);
  const aplicarModelo = (id) => {
    const m = emModelos.find((x) => x.id === Number(id));
    if (!m) return;
    setEmAssunto(m.assunto || "");
    setEmCorpo(m.corpo || "");
    setEmModeloKey((k) => k + 1);
  };
  const salvarComoModelo = async () => {
    if (!emAssunto.trim() || !emCorpo.trim()) { setEmErro("Escreva o assunto e o corpo antes de salvar como modelo."); return; }
    const nome = window.prompt("Nome do modelo (ex.: Convite direito de resposta):");
    if (!nome || !nome.trim()) return;
    try {
      await adminCriarModelo({ caso: "EmpresaCitada", nome: nome.trim(), assunto: emAssunto.trim(), corpo: emCorpo });
      const ms = await adminListarModelos();
      setEmModelos((ms || []).filter((m) => m.caso === "EmpresaCitada"));
      setEmMsg(`Modelo "${nome.trim()}" salvo. Já aparece no seletor.`);
    } catch (e) { setEmErro(e.message || "Falha ao salvar o modelo."); }
  };
  const enviarEmailResponsavel = async () => {
    if (!emAssunto.trim() || !emCorpo.trim()) { setEmErro("Preencha o assunto e o teor do e-mail."); return; }
    setEmBusy(true); setEmErro(null); setEmMsg(null);
    try {
      const r = await adminEmailResponsavel(d.id, { assunto: emAssunto.trim(), corpo: emCorpo.trim(), copiarCandidato: emCopiar });
      if (!r.ok) { setEmErro(r.erro || "Não foi possível enviar."); return; }
      let m = `Enviado ✅ ao responsável (${r.paraResponsavel}).`;
      if (r.candidatoCopiado) m += " Candidato em cópia.";
      else if (emCopiar && r.candidatoTemEmail === false) m += " (candidato sem e-mail no cadastro — não copiado)";
      m += " Você recebeu a [cópia].";
      setEmMsg(m);
      setEmAssunto(""); setEmCorpo(""); setEmCopiar(false); setEmailAberto(false);
      onAcao && onAcao();
    } catch (e) { setEmErro(e.message || "Falha ao enviar."); }
    finally { setEmBusy(false); }
  };

  // Admin: WhatsApp livre ao RESPONSÁVEL do processo (WhatsApp do contato do relato), em nome da eaiRH.
  const [waAberto, setWaAberto] = useState(false);
  const [waTexto, setWaTexto] = useState("");
  const [waBusy, setWaBusy] = useState(false);
  const [waMsg, setWaMsg] = useState(null);
  const [waErro, setWaErro] = useState(null);
  // Modelos de WhatsApp (caso "EmpresaCitada" / público "empresa") para preencher o compositor num clique + salvar novos.
  const [waModelos, setWaModelos] = useState([]);
  useEffect(() => {
    if (!waAberto) return;
    adminListarModelosWpp().then((ms) => setWaModelos((ms || []).filter((m) => m.caso === "EmpresaCitada" && m.publico === "empresa"))).catch(() => {});
  }, [waAberto]);
  const salvarComoModeloWpp = async () => {
    if (!waTexto.trim()) { setWaErro("Escreva a mensagem antes de salvar como modelo."); return; }
    const nome = window.prompt("Nome do modelo (ex.: Convite direito de resposta):");
    if (!nome || !nome.trim()) return;
    try {
      await adminCriarModeloWpp({ caso: "EmpresaCitada", publico: "empresa", nome: nome.trim(), corpo: waTexto.trim() });
      const ms = await adminListarModelosWpp();
      setWaModelos((ms || []).filter((m) => m.caso === "EmpresaCitada" && m.publico === "empresa"));
      setWaMsg(`Modelo "${nome.trim()}" salvo. Já aparece no seletor.`);
    } catch (e) { setWaErro(e.message || "Falha ao salvar o modelo."); }
  };
  const enviarWhatsappResponsavel = async () => {
    if (!waTexto.trim()) { setWaErro("Escreva o teor da mensagem."); return; }
    setWaBusy(true); setWaErro(null); setWaMsg(null);
    try {
      const r = await adminWhatsappResponsavel(d.id, { texto: waTexto.trim() });
      if (!r.ok) { setWaErro(r.erro || "Não foi possível enviar."); return; }
      setWaMsg(`Enviado ✅ ao responsável (${r.paraResponsavel}).`);
      setWaTexto(""); setWaAberto(false);
      onAcao && onAcao();
    } catch (e) { setWaErro(e.message || "Falha ao enviar."); }
    finally { setWaBusy(false); }
  };

  const lista = d.atualizacoes || [];
  if (!isAdmin && !d.ehMeu && lista.length === 0) return null;

  return (
    <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50/40 p-3">
      <div className="text-xs font-bold text-primary mb-2">Atualizações da eaiRH</div>
      {lista.length === 0 && <p className="text-xs text-gray-400 mb-2">Nenhuma atualização ainda.</p>}
      <div className="space-y-2">
        {lista.map((a) => (
          <div key={a.id} className={`rounded-lg border p-2.5 ${a.doCandidato ? "bg-amber-50 border-amber-100 sm:ml-6" : "bg-white border-gray-100"}`}>
            <div className={`text-[11px] font-bold mb-1 ${a.doCandidato ? "text-amber-700" : "text-primary"}`}>
              {a.doCandidato ? "Resposta do candidato" : "eaiRH"}
            </div>
            <p className="text-sm text-gray-700 whitespace-pre-line">{a.texto}</p>
            {a.temImagem && (
              <img src={urlImagemAtualizacao(d.id, a.id)} alt="anexo da atualização" loading="lazy"
                onClick={() => setZoom(urlImagemAtualizacao(d.id, a.id))}
                title="Clique para ampliar"
                className="mt-2 max-h-64 rounded-lg border border-gray-200 cursor-zoom-in hover:opacity-90 transition" />
            )}
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-400">
              <span>{fmt(a.criadoEm)}</span>
              {!a.publica && <span className="rounded-full bg-gray-100 text-gray-500 px-2 py-0.5">privada</span>}
              {isAdmin && (
                <span className="ml-auto flex items-center gap-1.5">
                  <button onClick={() => alternarVisibilidade(a)} disabled={acaoId === a.id}
                    className="rounded border border-gray-300 px-2 py-0.5 font-semibold hover:bg-gray-50 disabled:opacity-50"
                    title={a.publica ? "Tirar do público (só o candidato vê)" : "Tornar visível a todos"}>
                    {a.publica ? "🔒 Tornar privada" : "🌐 Tornar pública"}
                  </button>
                  <button onClick={() => apagarAtualizacao(a)} disabled={acaoId === a.id}
                    className="rounded border border-red-200 text-red-600 px-2 py-0.5 font-semibold hover:bg-red-50 disabled:opacity-50"
                    title="Apagar esta atualização">
                    🗑 Apagar
                  </button>
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {isAdmin && (
        <div className="mt-3 border-t border-blue-100 pt-3">
          <textarea value={texto} onChange={(e) => setTexto(e.target.value)} onPaste={onPaste} rows={3}
            placeholder="Escreva uma atualização em nome da eaiRH… (pode colar um print aqui)"
            className="w-full rounded-lg border border-gray-300 p-2 text-sm" />
          {preview && (
            <div className="mt-2 flex items-center gap-2">
              <img src={preview} alt="pré-visualização do print" className="max-h-24 rounded border border-gray-200" />
              <button onClick={() => { setImagem(null); setPreview(null); }} className="text-xs text-red-600 hover:underline">remover</button>
            </div>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
            <label className="inline-flex items-center gap-1"><input type="radio" name={`vis-${d.id}`} checked={publica} onChange={() => setPublica(true)} /> Pública (todos veem)</label>
            <label className="inline-flex items-center gap-1"><input type="radio" name={`vis-${d.id}`} checked={!publica} onChange={() => setPublica(false)} /> Só o candidato</label>
            <label className="inline-flex items-center gap-1 cursor-pointer text-primary font-semibold">
              📎 anexar print
              <input type="file" accept="image/*" className="hidden" onChange={(e) => anexar(e.target.files?.[0])} />
            </label>
            <button onClick={publicar} disabled={enviando}
              className="ml-auto rounded-lg bg-primary text-white px-3 py-1.5 font-semibold disabled:opacity-50">
              {enviando ? "Publicando…" : "Publicar atualização"}
            </button>
          </div>
          {msg && <p className="mt-2 text-xs text-green-700">{msg}</p>}
          {erro && <p className="mt-2 text-xs text-red-600">{erro}</p>}
        </div>
      )}

      {/* Admin: e-mail direto ao responsável do processo (contato do relato), pela marca eaiRH, com [cópia] automática */}
      {isAdmin && d.contatoResponsavel && (
        <div className="mt-3 border-t border-blue-100 pt-3">
          {!emailAberto ? (
            <button onClick={() => setEmailAberto(true)}
              className="rounded-lg border border-primary text-primary px-3 py-1.5 text-sm font-semibold hover:bg-primary hover:text-white transition">
              ✉️ Enviar e-mail ao responsável
            </button>
          ) : (
            <div>
              <div className="text-[11px] font-bold text-primary mb-1">
                E-mail ao responsável — <span className="font-normal text-gray-500">para {d.contatoResponsavel}</span>
              </div>
              {/* Seletor de modelo pronto + salvar o atual como modelo novo */}
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <select defaultValue="" onChange={(e) => { aplicarModelo(e.target.value); e.target.value = ""; }}
                  className="rounded-lg border border-gray-300 px-2 py-1.5 text-xs text-gray-700">
                  <option value="">Usar um modelo pronto…</option>
                  {emModelos.map((m) => <option key={m.id} value={m.id}>{m.nome}{m.padrao ? " ✓" : ""}</option>)}
                </select>
                <button type="button" onClick={salvarComoModelo}
                  className="rounded-lg border border-gray-300 text-gray-600 px-2 py-1.5 text-xs font-semibold hover:bg-gray-50">
                  Salvar como modelo
                </button>
                {emModelos.length === 0 && <span className="text-[11px] text-gray-400">nenhum modelo salvo ainda</span>}
              </div>
              <input value={emAssunto} onChange={(e) => setEmAssunto(e.target.value)}
                placeholder="Assunto (ex.: A [empresa] foi citada — vaga [vaga])"
                className="w-full rounded-lg border border-gray-300 p-2 text-sm mb-2" />
              <HtmlEditor key={emModeloKey} initialHtml={emCorpo} onChange={setEmCorpo} tags={TAGS} />
              <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
                <label className="inline-flex items-center gap-1"><input type="checkbox" checked={emCopiar} onChange={(e) => setEmCopiar(e.target.checked)} /> Copiar o candidato (CC)</label>
                <button onClick={() => { setEmailAberto(false); setEmErro(null); }} className="text-gray-500 hover:underline">cancelar</button>
                <button onClick={enviarEmailResponsavel} disabled={emBusy}
                  className="ml-auto rounded-lg bg-primary text-white px-3 py-1.5 font-semibold disabled:opacity-50">
                  {emBusy ? "Enviando…" : "Enviar e-mail"}
                </button>
              </div>
              <p className="text-[11px] text-gray-400 mt-1">Sai de contato@eairh e você recebe uma [cópia] automática.</p>
            </div>
          )}
          {emMsg && <p className="mt-2 text-xs text-green-700">{emMsg}</p>}
          {emErro && <p className="mt-2 text-xs text-red-600">{emErro}</p>}
        </div>
      )}

      {/* Admin: WhatsApp direto ao responsável do processo (WhatsApp do contato do relato), pela marca eaiRH */}
      {isAdmin && d.contatoWhatsappResponsavel && (
        <div className="mt-3 border-t border-blue-100 pt-3">
          {!waAberto ? (
            <button onClick={() => setWaAberto(true)}
              className="rounded-lg border border-primary text-primary px-3 py-1.5 text-sm font-semibold hover:bg-primary hover:text-white transition">
              💬 Enviar WhatsApp ao responsável
            </button>
          ) : (
            <div>
              <div className="text-[11px] font-bold text-primary mb-1">
                WhatsApp ao responsável — <span className="font-normal text-gray-500">para {d.contatoWhatsappResponsavel}</span>
              </div>
              {/* Seletor de modelo pronto + salvar o atual como modelo novo */}
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <select defaultValue="" onChange={(e) => { const m = waModelos.find((x) => x.id === Number(e.target.value)); if (m) setWaTexto(m.corpo || ""); e.target.value = ""; }}
                  className="rounded-lg border border-gray-300 px-2 py-1.5 text-xs text-gray-700">
                  <option value="">Usar um modelo pronto…</option>
                  {waModelos.map((m) => <option key={m.id} value={m.id}>{m.nome}{m.padrao ? " ✓" : ""}</option>)}
                </select>
                <button type="button" onClick={salvarComoModeloWpp}
                  className="rounded-lg border border-gray-300 text-gray-600 px-2 py-1.5 text-xs font-semibold hover:bg-gray-50">
                  Salvar como modelo
                </button>
                {waModelos.length === 0 && <span className="text-[11px] text-gray-400">nenhum modelo salvo ainda</span>}
              </div>
              <WhatsappEditor value={waTexto} onChange={setWaTexto} tags={TAGS} rows={5}
                placeholder="Escreva o teor da mensagem… (use *negrito*, _itálico_ e as tags; sai pela marca eaiRH)" />
              <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
                <button onClick={() => { setWaAberto(false); setWaErro(null); }} className="text-gray-500 hover:underline">cancelar</button>
                <button onClick={enviarWhatsappResponsavel} disabled={waBusy}
                  className="ml-auto rounded-lg bg-primary text-white px-3 py-1.5 font-semibold disabled:opacity-50">
                  {waBusy ? "Enviando…" : "Enviar WhatsApp"}
                </button>
              </div>
              <p className="text-[11px] text-gray-400 mt-1">Enquanto estivermos no Twilio sandbox, a mensagem é roteada para o número de teste.</p>
            </div>
          )}
          {waMsg && <p className="mt-2 text-xs text-green-700">{waMsg}</p>}
          {waErro && <p className="mt-2 text-xs text-red-600">{waErro}</p>}
        </div>
      )}

      {/* Candidato (autor do relato): responde os questionamentos da eaiRH — fica privado e avisa a equipe */}
      {d.ehMeu && (
        <div className="mt-3 border-t border-blue-100 pt-3">
          <div className="text-[11px] font-bold text-amber-700 mb-1">Responder à eaiRH</div>
          <textarea value={respTexto} onChange={(e) => setRespTexto(e.target.value)} rows={3}
            placeholder="Escreva sua resposta para a equipe eaiRH…"
            className="w-full rounded-lg border border-gray-300 p-2 text-sm" />
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button onClick={responder} disabled={respBusy}
              className="rounded-lg bg-primary text-white px-3 py-1.5 text-sm font-semibold disabled:opacity-50">
              {respBusy ? "Enviando…" : "Enviar resposta"}
            </button>
            {respMsg && <span className="text-xs text-green-700">{respMsg}</span>}
            {respErro && <span className="text-xs text-red-600">{respErro}</span>}
          </div>
          <p className="text-[11px] text-gray-400 mt-1">Sua resposta fica visível só para você e a equipe eaiRH.</p>
        </div>
      )}

      {/* Lightbox: clique na imagem abre o print ampliado; clicar fora ou Esc fecha */}
      {zoom && (
        <div onClick={() => setZoom(null)}
          className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4 cursor-zoom-out">
          <img src={zoom} alt="print ampliado" className="max-h-full max-w-full rounded-lg shadow-2xl" />
          <button onClick={() => setZoom(null)} aria-label="Fechar"
            className="absolute top-4 right-5 text-white text-4xl leading-none hover:opacity-80">×</button>
        </div>
      )}
    </div>
  );
}

export function DenunciaCard({ d, user, souDono, onAcao, permalink = false }) {
  const [aba, setAba] = useState(null); // 'responder' | 'avaliar' | 'contestar' | 'treplicar' | 'contestar-empresa' | null
  const [reportando, setReportando] = useState(false);
  const [reportado, setReportado] = useState(false);
  const [confs, setConfs] = useState(d.totalConfirmacoes || 0);
  const [confFeito, setConfFeito] = useState(false);
  const [confErro, setConfErro] = useState(null);
  const [checkinErro, setCheckinErro] = useState(null);
  const [checkinBusy, setCheckinBusy] = useState(false);
  const ehAutor = user && !souDono && d.ehMeu;     // candidato dono daquela denúncia (servidor marca, respeita anonimato)
  const podeConfirmar = user?.tipo === 0 && !souDono && !d.ehMeu; // candidato que NÃO é o autor
  const podeContestarEmpresa = souDono && !d.contestadaPelaEmpresa;
  // Candidato avalia após a empresa responder — E pode REAVALIAR depois (rever a nota à luz da resposta/tréplica).
  // A reputação reflete a visão ATUAL de quem viveu o processo, não a foto de um momento. Também libera reavaliar
  // um caso já encerrado sem resposta, caso a empresa responda depois.
  const podeAvaliar = ehAutor && (d.resposta || d.avaliacao);
  const podeEncerrarSemResposta = ehAutor && !d.avaliacao && !d.resposta; // autor encerra com nota mesmo sem a empresa responder
  const podeResponder = souDono && !d.resposta;                  // empresa responde
  const podeContestar = ehAutor && d.resposta && !d.avaliacao && !d.contestacaoTexto; // réplica
  const podeTreplicar = souDono && d.contestacaoTexto && !d.treplicaTexto;            // tréplica
  const podeCheckin = ehAutor && d.confirmacaoRetornoSolicitada && d.houveRetorno == null && !d.resposta;

  // Linha do tempo do caso (só etapas que aconteceram)
  const eventos = [
    { t: "Registrada", q: d.criadoEm, on: true },
    { t: "Resposta", q: d.resposta?.criadoEm, on: !!d.resposta },
    { t: "Réplica", q: d.contestacaoEm, on: !!d.contestacaoTexto },
    { t: "Tréplica", q: d.treplicaEm, on: !!d.treplicaTexto },
    { t: "Avaliação", q: d.avaliacao?.criadoEm, on: !!d.avaliacao },
  ].filter((e) => e.on);

  return (
    <article className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-bold text-gray-900">{d.titulo}</h3>
          <p className="text-xs text-gray-500">{d.vaga ? `${d.vaga} · ` : ""}{d.etapaTexto} · {fmt(d.criadoEm)}</p>
        </div>
        {/* Status (de reclamação) só para reclamação; elogio/neutro mostram a natureza. */}
        {d.natureza === 1
          ? <span className="text-xs font-semibold rounded-full bg-green-100 text-green-700 px-2.5 py-1 shrink-0">😊 Elogio</span>
          : d.natureza === 2
          ? <span className="text-xs font-semibold rounded-full bg-gray-100 text-gray-600 px-2.5 py-1 shrink-0">😐 Relato</span>
          : <span className="text-white text-xs font-semibold rounded-full px-2.5 py-1 shrink-0" style={{ backgroundColor: d.statusCor }}>{d.statusTexto}</span>}
      </div>

      <p className="mt-3 text-gray-700 text-sm">{d.descricao}</p>
      <p className="text-xs text-gray-400 mt-2">por {d.autorNome}</p>
      {d.candidatoNome && (
        <p className="mt-2 inline-block rounded-lg bg-blue-50 border border-blue-100 px-2.5 py-1.5 text-xs text-blue-900">
          👤 Candidato: <b>{d.candidatoNome}</b>
          <span className="block text-[11px] text-blue-700/80">Visível só para a empresa citada, para localizar o processo internamente e poder se posicionar. Não aparece em público.</span>
        </p>
      )}

      {/* Dados estruturados do processo (estilo Glassdoor) */}
      {(d.dificuldadeProcesso || d.desfecho > 0 || d.tipoRetorno > 0) && (
        <div className="mt-2 flex flex-wrap gap-2">
          {d.dificuldadeProcesso && (
            <span className="text-xs font-semibold rounded-full bg-purple-100 text-purple-700 px-2.5 py-1">
              Dificuldade {d.dificuldadeProcesso}/5
            </span>
          )}
          {d.desfecho > 0 && (
            <span className="text-xs font-semibold rounded-full bg-slate-100 text-slate-700 px-2.5 py-1">
              {d.desfechoTexto}
            </span>
          )}
          {d.tipoRetorno > 0 && (
            <span className="text-xs font-semibold rounded-full bg-amber-100 text-amber-800 px-2.5 py-1">
              {d.tipoRetornoTexto}
            </span>
          )}
        </div>
      )}
      {/* Perguntas/etapas da entrevista: só na reclamação na íntegra (mantém a lista enxuta) */}
      {!permalink && d.perguntasEntrevista && (
        <div className="mt-2 rounded-lg bg-gray-50 border border-gray-200 p-2.5">
          <span className="text-xs font-bold text-gray-500">Perguntas/etapas da entrevista</span>
          <p className="text-sm text-gray-700">{d.perguntasEntrevista}</p>
        </div>
      )}

      {/* Credibilidade: "também aconteceu comigo" */}
      <div className="mt-3 flex flex-wrap items-center gap-3">
        {confs > 0 && (
          <span className="text-xs font-semibold text-gray-600">
            👍 {confs} {confs === 1 ? "pessoa também passou" : "pessoas também passaram"} por isso
          </span>
        )}
        {podeConfirmar && !confFeito && (
          <button
            onClick={async () => {
              setConfErro(null);
              try { const r = await confirmarRelato(d.id); setConfs(r.total); setConfFeito(true); }
              catch (e) { if (/já confirmou/i.test(e.message)) setConfFeito(true); else setConfErro(e.message); }
            }}
            className="text-xs font-semibold rounded-full border border-primary text-primary px-3 py-1 hover:bg-primary hover:text-white transition">
            Também aconteceu comigo
          </button>
        )}
        {confFeito && <span className="text-xs text-green-700">✓ você confirmou</span>}
        {confErro && <span className="text-xs text-red-600">{confErro}</span>}
      </div>

      {/* Contestação da empresa (direito de resposta — vai para moderação) */}
      {d.contestadaPelaEmpresa && (
        <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3">
          <div className="text-xs font-bold text-amber-800">⚠️ A empresa contesta a veracidade deste relato — em análise pela moderação.</div>
          {d.contestacaoEmpresaTexto && <p className="text-sm text-gray-700 mt-1">"{d.contestacaoEmpresaTexto}"</p>}
        </div>
      )}

      {d.prazoExpirado && !d.resposta && (
        <p className="mt-2 text-xs font-semibold text-red-600">⏰ Prazo de {d.prazoRespostaDias} dias expirado sem resposta</p>
      )}

      {/* Esforço da eaiRH: quando convidamos a empresa ao direito de resposta, dizer isso junto do prazo.
          Não apaga o silêncio (o selo vermelho fica) — só mostra que houve convite antes da exposição. */}
      {d.empresaContatadaEm && !d.resposta && (
        <p className="mt-1 text-xs font-semibold text-blue-700">✉️ A eaiRH convidou a empresa a responder em {fmt(d.empresaContatadaEm)}</p>
      )}

      {/* Resposta da empresa */}
      {d.resposta && (
        <div className="mt-3 border-l-4 border-primary bg-blue-50 rounded-r-lg p-3">
          <div className="text-xs font-bold text-primary mb-1">Resposta da empresa</div>
          <p className="text-sm text-gray-700">{d.resposta.texto}</p>
        </div>
      )}

      {/* Réplica do candidato */}
      {d.contestacaoTexto && (
        <div className="mt-3 border-l-4 border-yellow-500 bg-yellow-50 rounded-r-lg p-3">
          <div className="text-xs font-bold text-yellow-700 mb-1">Réplica do candidato</div>
          <p className="text-sm text-gray-700">{d.contestacaoTexto}</p>
        </div>
      )}

      {/* Tréplica da empresa (última palavra) */}
      {d.treplicaTexto && (
        <div className="mt-3 border-l-4 border-primary bg-blue-50 rounded-r-lg p-3">
          <div className="text-xs font-bold text-primary mb-1">Tréplica da empresa</div>
          <p className="text-sm text-gray-700">{d.treplicaTexto}</p>
        </div>
      )}

      {/* Avaliação do candidato */}
      {d.avaliacao && (
        <div className="mt-3 bg-yellow-50 rounded-lg p-3 text-sm">
          ⭐ <b>{d.avaliacao.nota}/10</b> · {d.avaliacao.resolucao === 2 ? "Resolvido" : d.avaliacao.resolucao === 1 ? "Resolvido parcialmente" : "Não resolvido"} ·
          {d.avaliacao.voltariaSeCandidatar ? " Voltaria" : " Não voltaria"}
          {d.avaliacao.comentario && <p className="text-gray-600 mt-1">"{d.avaliacao.comentario}"</p>}
        </div>
      )}

      {/* Check-in: a empresa deu retorno? */}
      {podeCheckin && (
        <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-3">
          <p className="text-sm font-semibold text-amber-800 mb-2">O prazo está acabando. {d.empresa} te deu retorno?</p>
          <div className="flex gap-2">
            <button disabled={checkinBusy} onClick={async () => { if (checkinBusy) return; setCheckinErro(null); setCheckinBusy(true); try { await confirmarRetorno(d.id, true); onAcao(); } catch (e) { setCheckinErro(e.message); setCheckinBusy(false); } }}
              className="px-3 py-1.5 rounded-lg bg-green-600 disabled:opacity-60 text-white text-sm font-semibold hover:bg-green-700">Sim, deu retorno</button>
            <button disabled={checkinBusy} onClick={async () => { if (checkinBusy) return; setCheckinErro(null); setCheckinBusy(true); try { await confirmarRetorno(d.id, false); onAcao(); } catch (e) { setCheckinErro(e.message); setCheckinBusy(false); } }}
              className="px-3 py-1.5 rounded-lg bg-red-600 disabled:opacity-60 text-white text-sm font-semibold hover:bg-red-700">Não, sumiu</button>
          </div>
          {checkinErro && <p className="text-red-600 text-xs mt-2">{checkinErro}</p>}
        </div>
      )}

      {/* Atualizações da eaiRH (timeline do caso) + caixa de publicar (admin) */}
      <AtualizacoesEaiRH d={d} isAdmin={user?.ehAdmin === true} onAcao={onAcao} />

      {/* Linha do tempo */}
      {eventos.length > 1 && (
        <div className="mt-4 flex flex-wrap items-center gap-1 text-xs text-gray-500">
          {eventos.map((e, i) => (
            <span key={e.t} className="flex items-center gap-1">
              {i > 0 && <span className="text-gray-300">→</span>}
              <span className="inline-flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-primary inline-block" />
                {e.t} <span className="text-gray-400">{fmt(e.q)}</span>
              </span>
            </span>
          ))}
        </div>
      )}

      {/* Ver na íntegra + compartilhar (link com preview = /relato/:id) */}
      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
        {permalink && (
          <Link to={`/relato/${d.id}`} className="text-sm font-semibold text-primary hover:underline">Ver relato na íntegra →</Link>
        )}
        <span className="text-xs font-semibold text-gray-500 sm:ml-auto">Compartilhar:</span>
        <ShareButtons compact url={`/relato/${d.id}`} text={`Relato sobre o processo seletivo da ${d.empresa || "empresa"} no eaiRH:`} />
      </div>

      {/* Botões de ação */}
      <div className="mt-3 flex flex-wrap gap-2">
        {podeResponder && <BtnAcao onClick={() => setAba(aba === "responder" ? null : "responder")}>Responder</BtnAcao>}
        {podeContestar && <BtnAcao onClick={() => setAba(aba === "contestar" ? null : "contestar")}>Contestar resposta</BtnAcao>}
        {podeTreplicar && <BtnAcao onClick={() => setAba(aba === "treplicar" ? null : "treplicar")}>Tréplica</BtnAcao>}
        {podeContestarEmpresa && <BtnAcao onClick={() => setAba(aba === "contestar-empresa" ? null : "contestar-empresa")}>Contestar veracidade</BtnAcao>}
        {podeAvaliar && <BtnAcao onClick={() => setAba(aba === "avaliar" ? null : "avaliar")}>{d.avaliacao ? "Rever avaliação" : "Avaliar experiência"}</BtnAcao>}
        {podeEncerrarSemResposta && <BtnAcao onClick={() => setAba(aba === "encerrar" ? null : "encerrar")}>Encerrar relato</BtnAcao>}
        {user && !reportado && (
          <button onClick={() => setReportando((v) => !v)}
            className="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-500 text-sm hover:bg-gray-50 ml-auto">⚑ Reportar</button>
        )}
        {reportado && <span className="text-xs text-gray-400 ml-auto self-center">Reporte recebido. Obrigado.</span>}
      </div>

      {reportando && !reportado && (
        <FormReporte id={d.id} onPronto={() => { setReportando(false); setReportado(true); onAcao(); }} />
      )}

      {aba === "responder" && <FormResposta id={d.id} onPronto={() => { setAba(null); onAcao(); }} />}
      {aba === "contestar" && <FormContestar id={d.id} onPronto={() => { setAba(null); onAcao(); }} />}
      {aba === "treplicar" && <FormTreplica id={d.id} onPronto={() => { setAba(null); onAcao(); }} />}
      {aba === "contestar-empresa" && <FormContestarEmpresa id={d.id} onPronto={() => { setAba(null); onAcao(); }} />}
      {aba === "avaliar" && <FormAvaliacao id={d.id} atual={d.avaliacao} onPronto={() => { setAba(null); onAcao(); }} />}
      {aba === "encerrar" && <FormEncerrar id={d.id} onPronto={() => { setAba(null); onAcao(); }} />}
    </article>
  );
}

function FormTreplica({ id, onPronto }) {
  const [texto, setTexto] = useState("");
  const [erro, setErro] = useState(null);
  const [enviando, setEnviando] = useState(false);
  return (
    <div className="mt-3">
      {erro && <p role="alert" className="text-red-600 text-sm mb-1">{erro}</p>}
      <textarea value={texto} onChange={(e) => setTexto(e.target.value)} rows="3"
        className="w-full rounded-lg border border-gray-300 p-2 text-sm focus:ring-2 focus:ring-primary outline-none"
        placeholder="Última palavra da empresa após a réplica do candidato..." />
      <button
        disabled={enviando}
        onClick={async () => { if (enviando) return; setEnviando(true); try { await treplicarDenuncia(id, texto); onPronto(); } catch (e) { setErro(e.message); setEnviando(false); } }}
        className="mt-2 px-4 py-1.5 rounded-lg bg-primary disabled:opacity-60 text-white text-sm font-semibold">Enviar tréplica</button>
    </div>
  );
}

function FormContestarEmpresa({ id, onPronto }) {
  const [texto, setTexto] = useState("");
  const [erro, setErro] = useState(null);
  const [enviando, setEnviando] = useState(false);
  return (
    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
      {erro && <p role="alert" className="text-red-600 text-sm mb-1">{erro}</p>}
      <p className="text-xs text-gray-600 mb-2">
        Conteste a <strong>veracidade</strong> do relato (ex.: a pessoa não participou do processo, fatos incorretos). Vai para a moderação.
        O relato continua visível, marcado como "em análise".
      </p>
      <textarea value={texto} onChange={(e) => setTexto(e.target.value)} rows="3"
        className="w-full rounded-lg border border-gray-300 p-2 text-sm focus:ring-2 focus:ring-amber-500 outline-none"
        placeholder="Explique objetivamente por que o relato é falso/incorreto..." />
      <button disabled={enviando}
        onClick={async () => { if (enviando) return; setEnviando(true); try { await contestarEmpresa(id, texto); onPronto(); } catch (e) { setErro(e.message); setEnviando(false); } }}
        className="mt-2 px-4 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white text-sm font-semibold">Enviar contestação</button>
    </div>
  );
}

function FormReporte({ id, onPronto }) {
  const [motivo, setMotivo] = useState(MOTIVOS_REPORTE[0]);
  const [erro, setErro] = useState(null);
  const [enviando, setEnviando] = useState(false);
  return (
    <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
      {erro && <p role="alert" className="text-red-600 text-sm mb-1">{erro}</p>}
      <label className="block text-xs font-semibold text-gray-600 mb-1">Por que reportar este conteúdo?</label>
      <div className="flex gap-2">
        <select value={motivo} onChange={(e) => setMotivo(e.target.value)} aria-label="Motivo do reporte"
          className="flex-1 rounded-lg border border-gray-300 p-2 text-sm outline-none focus:ring-2 focus:ring-primary">
          {MOTIVOS_REPORTE.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <button
          disabled={enviando}
          onClick={async () => { if (enviando) return; setEnviando(true); try { await reportarDenuncia(id, motivo); onPronto(); } catch (e) { setErro(e.message); setEnviando(false); } }}
          className="px-4 py-2 rounded-lg bg-gray-700 disabled:opacity-60 text-white text-sm font-semibold hover:bg-gray-800">{enviando ? "Enviando..." : "Enviar"}</button>
      </div>
    </div>
  );
}

function BtnAcao({ children, onClick }) {
  return <button onClick={onClick} className="px-3 py-1.5 rounded-lg border border-primary text-primary text-sm font-semibold hover:bg-primary hover:text-white transition">{children}</button>;
}

function FormResposta({ id, onPronto }) {
  const [texto, setTexto] = useState("");
  const [erro, setErro] = useState(null);
  const [enviando, setEnviando] = useState(false);
  return (
    <div className="mt-3">
      {erro && <p role="alert" className="text-red-600 text-sm mb-1">{erro}</p>}
      <textarea value={texto} onChange={(e) => setTexto(e.target.value)} rows="3"
        className="w-full rounded-lg border border-gray-300 p-2 text-sm focus:ring-2 focus:ring-primary outline-none"
        placeholder="Escreva a resposta pública da empresa..." />
      <button
        disabled={enviando}
        onClick={async () => { if (enviando) return; setEnviando(true); try { await responderDenuncia(id, texto); onPronto(); } catch (e) { setErro(e.message); setEnviando(false); } }}
        className="mt-2 px-4 py-1.5 rounded-lg bg-primary disabled:opacity-60 text-white text-sm font-semibold">{enviando ? "Publicando..." : "Publicar resposta"}</button>
    </div>
  );
}

function FormContestar({ id, onPronto }) {
  const [texto, setTexto] = useState("");
  const [erro, setErro] = useState(null);
  const [enviando, setEnviando] = useState(false);
  return (
    <div className="mt-3">
      {erro && <p role="alert" className="text-red-600 text-sm mb-1">{erro}</p>}
      <textarea value={texto} onChange={(e) => setTexto(e.target.value)} rows="3"
        className="w-full rounded-lg border border-gray-300 p-2 text-sm focus:ring-2 focus:ring-yellow-500 outline-none"
        placeholder="Por que a resposta da empresa não resolveu seu caso?" />
      <button
        disabled={enviando}
        onClick={async () => { if (enviando) return; setEnviando(true); try { await contestarDenuncia(id, texto); onPronto(); } catch (e) { setErro(e.message); setEnviando(false); } }}
        className="mt-2 px-4 py-1.5 rounded-lg bg-yellow-500 disabled:opacity-60 text-white text-sm font-semibold">Enviar réplica</button>
    </div>
  );
}

function FormAvaliacao({ id, atual, onPronto }) {
  // Reavaliação: pré-carrega a avaliação atual pra o candidato ajustar (não recomeçar do zero).
  const [nota, setNota] = useState(atual ? atual.nota : 8);
  const [resolucao, setResolucao] = useState(atual ? atual.resolucao : 2); // 0=não, 1=parcial, 2=resolvido
  const [voltaria, setVoltaria] = useState(atual ? atual.voltariaSeCandidatar : true);
  const [comentario, setComentario] = useState(atual?.comentario || "");
  const [erro, setErro] = useState(null);
  const [enviando, setEnviando] = useState(false);

  return (
    <div className="mt-3 space-y-2">
      {atual && <p className="text-xs text-gray-500">Você já avaliou este caso. Ajuste como achar justo — sua nota reflete sua visão atual, e a empresa é avisada da mudança.</p>}
      {erro && <p role="alert" className="text-red-600 text-sm">{erro}</p>}
      <label className="block text-sm font-semibold text-gray-700">Seu caso foi resolvido?</label>
      <SeletorResolucao valor={resolucao} onChange={setResolucao} />
      <label className="block text-sm font-semibold text-gray-700">Nota: {nota}/10</label>
      <input type="range" min="0" max="10" value={nota} onChange={(e) => setNota(Number(e.target.value))} className="w-full" />
      <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={voltaria} onChange={(e) => setVoltaria(e.target.checked)} /> Voltaria a se candidatar</label>
      <textarea value={comentario} onChange={(e) => setComentario(e.target.value)} rows="2"
        className="w-full rounded-lg border border-gray-300 p-2 text-sm focus:ring-2 focus:ring-primary outline-none"
        placeholder="Comentário (opcional)" />
      <button
        disabled={enviando}
        onClick={async () => { if (enviando) return; setEnviando(true);
          try { await avaliarDenuncia(id, { nota, resolucao, voltariaSeCandidatar: voltaria, comentario }); onPronto(); }
          catch (e) { setErro(e.message); setEnviando(false); }
        }}
        className="px-4 py-1.5 rounded-lg bg-orange-600 disabled:opacity-60 text-white text-sm font-semibold">{enviando ? "Enviando..." : atual ? "Salvar" : "Enviar avaliação"}</button>
    </div>
  );
}

// Seletor dos 3 status de resolução (estilo Reclame Aqui).
function SeletorResolucao({ valor, onChange }) {
  const opts = [
    { v: 0, t: "Não resolvido", cls: "bg-purple-600" },
    { v: 1, t: "Resolvido parcialmente", cls: "bg-lime-600" },
    { v: 2, t: "Resolvido", cls: "bg-green-600" },
  ];
  return (
    <div className="flex flex-wrap gap-2">
      {opts.map((o) => (
        <button key={o.v} type="button" onClick={() => onChange(o.v)}
          className={`px-3 py-1.5 rounded-lg text-sm font-semibold border ${valor === o.v ? `${o.cls} text-white border-transparent` : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"}`}>
          {o.t}
        </button>
      ))}
    </div>
  );
}

// Encerramento pelo autor quando a empresa NÃO respondeu (ex.: processos com sumiço).
// Usa o mesmo endpoint de avaliação; defaults refletem a falta de retorno.
function FormEncerrar({ id, onPronto }) {
  const [resolucao, setResolucao] = useState(0); // 0=não, 1=parcial, 2=resolvido
  const [nota, setNota] = useState(2);
  const [voltaria, setVoltaria] = useState(false);
  const [comentario, setComentario] = useState("");
  const [erro, setErro] = useState(null);
  const [enviando, setEnviando] = useState(false);

  // Ao escolher o status, sugere nota/voltaria coerentes (ajustáveis depois).
  function escolher(v) {
    setResolucao(v);
    setNota(v === 2 ? 8 : v === 1 ? 5 : 2);
    setVoltaria(v === 2);
  }

  return (
    <div className="mt-3 space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
      <p className="text-xs text-gray-500">
        Encerre o caso sem esperar a empresa responder por aqui. Use <b>Resolvido</b> ou <b>Resolvido parcialmente</b> se
        a empresa te retornou por fora (e-mail, telefone, WhatsApp); use <b>Não resolvido</b> se foi sumiço.
        A sua nota entra na reputação da empresa.
      </p>
      {erro && <p role="alert" className="text-red-600 text-sm">{erro}</p>}
      <label className="block text-sm font-semibold text-gray-700">Status do caso</label>
      <SeletorResolucao valor={resolucao} onChange={escolher} />
      <label className="block text-sm font-semibold text-gray-700">Nota: {nota}/10</label>
      <input type="range" min="0" max="10" value={nota} onChange={(e) => setNota(Number(e.target.value))} className="w-full" />
      <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={voltaria} onChange={(e) => setVoltaria(e.target.checked)} /> Voltaria a se candidatar</label>
      <textarea value={comentario} onChange={(e) => setComentario(e.target.value)} rows="2"
        className="w-full rounded-lg border border-gray-300 p-2 text-sm focus:ring-2 focus:ring-primary outline-none"
        placeholder="Comentário (opcional) — ex.: a empresa me retornou por e-mail, ou nunca obtive retorno." />
      <button
        disabled={enviando}
        onClick={async () => { if (enviando) return; setEnviando(true);
          try { await avaliarDenuncia(id, { nota, resolucao, voltariaSeCandidatar: voltaria, comentario }); onPronto(); }
          catch (e) { setErro(e.message); setEnviando(false); }
        }}
        className="px-4 py-1.5 rounded-lg bg-orange-600 disabled:opacity-60 text-white text-sm font-semibold">{enviando ? "Encerrando..." : "Encerrar relato"}</button>
    </div>
  );
}
